import { io, Socket } from 'socket.io-client';

// Socket event types
export type SocketEventType = 
  | 'enquiry:created' | 'enquiry:updated' | 'enquiry:deleted' | 'enquiry:restored'
  | 'application:created' | 'application:updated' | 'application:deleted' | 'application:restored'
  | 'resume:created' | 'resume:updated' | 'resume:deleted' | 'resume:restored'
  | 'career:created' | 'career:updated' | 'career:deleted' | 'career:restored'
  | 'user:created' | 'user:updated' | 'user:deleted';

export interface SocketEventData {
  type: SocketEventType;
  data: any;
  timestamp: string;
  userId?: string;
}

type EventCallback = (data: SocketEventData) => void;

class SocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<EventCallback>> = new Map();
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private isConnecting = false;
  private disconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  connect() {
    // Clear any pending disconnect (handles React StrictMode double-mount)
    if (this.disconnectTimeout) {
      clearTimeout(this.disconnectTimeout);
      this.disconnectTimeout = null;
    }

    if (this.socket?.connected) {
      console.log('Socket already connected');
      return;
    }

    if (this.isConnecting) {
      console.log('Socket connection in progress');
      return;
    }

    this.isConnecting = true;
    
    // Get socket URL from environment or use default
    const envUrl = (import.meta as any).env?.VITE_API_URL;
    const SOCKET_URL = envUrl?.replace('/api', '') || 'http://localhost:5000';
    
    this.socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 2000, // Increased from 1s to 2s (Render needs time to wake up)
      reconnectionDelayMax: 10000, // Max 10s between retries
      timeout: 20000, // Increased timeout to 20s (Render cold start can take 10-15s)
      // Force new connection to avoid stale connections
      forceNew: false,
      // Use polling first for better Render compatibility
      upgrade: true,
    });

    this.socket.on('connect', () => {
      console.log('🔌 Socket connected:', this.socket?.id);
      this.isConnected = true;
      this.isConnecting = false;
      this.reconnectAttempts = 0;

      // Join admin room with role
      const adminUser = localStorage.getItem('adminUser');
      if (adminUser) {
        try {
          const user = JSON.parse(adminUser);
          this.socket?.emit('join-admin', user.role || 'admin');
        } catch (e) {
          this.socket?.emit('join-admin', 'admin');
        }
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Socket disconnected:', reason);
      this.isConnected = false;
      this.isConnecting = false;
    });

    this.socket.on('connect_error', (error) => {
      this.reconnectAttempts++;
      this.isConnecting = false;
      
      // Only log detailed errors, not every timeout (reduces console spam)
      if (error.message !== 'timeout') {
        console.error('Socket connection error:', error.message);
      } else if (this.reconnectAttempts <= 2) {
        // Log first 2 timeout attempts (Render is waking up)
        console.log(`⏳ Waiting for server to wake up... (attempt ${this.reconnectAttempts})`);
      }
    });

    // Listen for all data events
    const eventTypes: SocketEventType[] = [
      'enquiry:created', 'enquiry:updated', 'enquiry:deleted', 'enquiry:restored',
      'application:created', 'application:updated', 'application:deleted', 'application:restored',
      'resume:created', 'resume:updated', 'resume:deleted', 'resume:restored',
      'career:created', 'career:updated', 'career:deleted', 'career:restored',
      'user:created', 'user:updated', 'user:deleted'
    ];

    eventTypes.forEach(eventType => {
      this.socket?.on(eventType, (data: any) => {
        const eventData: SocketEventData = {
          type: eventType,
          data,
          timestamp: new Date().toISOString()
        };
        this.notifyListeners(eventType, eventData);
      });
    });
  }

  disconnect() {
    // Delay disconnect to handle React StrictMode double-mount/unmount
    // If connect is called again before timeout, disconnect is cancelled
    this.disconnectTimeout = setTimeout(() => {
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
        this.isConnected = false;
        this.isConnecting = false;
        console.log('🔌 Socket manually disconnected');
      }
      this.disconnectTimeout = null;
    }, 100);
  }

  // Subscribe to specific events
  subscribe(eventType: SocketEventType | 'all', callback: EventCallback) {
    const key = eventType;
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)?.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(key)?.delete(callback);
    };
  }

  // Notify all listeners for an event
  private notifyListeners(eventType: SocketEventType, data: SocketEventData) {
    // Notify specific event listeners
    this.listeners.get(eventType)?.forEach(callback => callback(data));
    
    // Notify "all" listeners
    this.listeners.get('all')?.forEach(callback => callback(data));

    // Notify category listeners (e.g., 'enquiry' for 'enquiry:created')
    const category = eventType.split(':')[0];
    this.listeners.get(category as any)?.forEach(callback => callback(data));
  }

  // Subscribe to a category of events (enquiry, application, resume, career)
  subscribeToCategory(category: 'enquiry' | 'application' | 'resume' | 'career' | 'user', callback: EventCallback) {
    const key = category;
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)?.add(callback);

    return () => {
      this.listeners.get(key)?.delete(callback);
    };
  }

  getConnectionStatus() {
    return this.isConnected;
  }

  getSocket() {
    return this.socket;
  }
}

// Export singleton instance
export const socketService = new SocketService();
